import { useCallback } from 'react';
import isNil from 'lodash/isNil';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { validateElementField } from '../errorHandlers/validateElementField';
import { UpdateQuestionnaireElementPayload, QuestionnaireFormElement } from '../../questionnaireTemplate.types';

export function useUpdateElementValue<T extends string>() {
  // Actions are stable references — safe to subscribe, never cause re-renders
  const updateValue = QuestionnaireTemplateStore.useStoreActions((a) => a.updateElementValue);
  const setFieldError = QuestionnaireTemplateStore.useStoreActions((a) => a.setFieldError);
  const clearFieldError = QuestionnaireTemplateStore.useStoreActions((a) => a.clearFieldError);

  // ✅ useStoreDispatch gives access to the store instance itself.
  // getState() reads the current state at call-time with zero subscription — no re-renders.
  const store = QuestionnaireTemplateStore.useStore();

  return useCallback(
    (payload: UpdateQuestionnaireElementPayload<T>): void => {
      const { id, path, value } = payload;
      updateValue(payload);

      // Read the latest state at call-time — not via subscription
      const element: QuestionnaireFormElement | undefined = store.getState().entities.elements[id];
      if (element === undefined) return;

      const updatedElement = { ...element, [path]: value };
      const errorKey = `${id}.${path}`;
      const fieldError = validateElementField(updatedElement, path, value);

      if (!isNil(fieldError)) {
        setFieldError({ key: errorKey, error: { message: fieldError.message, context: fieldError.context } });
      } else {
        clearFieldError(errorKey);
      }
    },
    [store, updateValue, setFieldError, clearFieldError],
  );
}
